import * as React from 'react';

/**
 * Custom hook to detect if the current device is mobile based on the window width.
 *
 * This hook listens for window resizing and determines if the current window width is less than the defined
 * mobile breakpoint (768px). It returns a boolean value indicating whether the device is mobile.
 *
 * @returns {boolean} `true` if the device is considered mobile (width less than 768px), otherwise `false`.
 *
 * @example
 * const isMobile = useIsMobile();
 * console.log(isMobile); // true if the device width is less than 768px, false otherwise.
 */

export const MOBILE_BREAKPOINT = 768;
/** iPad and similar tablets use inline configure nav (same as mobile), not the desktop split panel. */
export const COMPACT_SIDEBAR_BREAKPOINT = 1024;

const getIsBelowBreakpoint = (breakpoint: number) =>
  typeof window !== 'undefined' && window.innerWidth < breakpoint;

/**
 * Prefer addEventListener; fall back to addListener for older WebKit/Android WebViews.
 * Always apply the current width first so a throwing subscribe path cannot leave UI stuck on desktop.
 */
export const subscribeViewportBreakpoint = (breakpoint: number, onChange: () => void) => {
  onChange();

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }

  const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);

  try {
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
  } catch {
    // Some WebViews expose addEventListener but reject the "change" event name.
  }

  if (typeof mql.addListener === 'function') {
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }

  return () => undefined;
};

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => getIsBelowBreakpoint(MOBILE_BREAKPOINT));

  React.useEffect(() => {
    return subscribeViewportBreakpoint(MOBILE_BREAKPOINT, () => {
      setIsMobile(getIsBelowBreakpoint(MOBILE_BREAKPOINT));
    });
  }, []);

  return isMobile;
}

export function useIsCompactSidebar() {
  const [isCompact, setIsCompact] = React.useState(() =>
    getIsBelowBreakpoint(COMPACT_SIDEBAR_BREAKPOINT)
  );

  React.useEffect(() => {
    return subscribeViewportBreakpoint(COMPACT_SIDEBAR_BREAKPOINT, () => {
      setIsCompact(getIsBelowBreakpoint(COMPACT_SIDEBAR_BREAKPOINT));
    });
  }, []);

  return isCompact;
}

export type VisualViewportBox = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** Layout viewport ≠ visible area on Android Chrome (S24 Ultra). Use the visual viewport. */
export const getVisualViewportBox = (): VisualViewportBox => {
  if (typeof window === 'undefined') {
    return { top: 0, left: 0, width: 0, height: 0 };
  }

  const vv = window.visualViewport;
  if (!vv) {
    return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
  }

  return {
    top: vv.offsetTop,
    left: vv.offsetLeft,
    width: vv.width,
    height: vv.height,
  };
};

export function useVisualViewportBox(enabled: boolean) {
  const [box, setBox] = React.useState(getVisualViewportBox);

  React.useEffect(() => {
    if (!enabled) return undefined;

    const update = () => setBox(getVisualViewportBox());
    update();

    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [enabled]);

  return box;
}
